import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Button } from './ui/button';
import { Shield, Menu, X, BookOpen, Mail, LogIn, LogOut } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import LoginModal from './auth/LoginModal';

export default function Header({ scrolled, onCTAClick }) {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [showLogin, setShowLogin]           = useState(false);
  const { user, logout, isAuthenticated }   = useAuth();

  const location    = useLocation();
  const isOnLanding = location.pathname === '/';
  const isOnBlog    = location.pathname.startsWith('/blog');
  const isOnContact = location.pathname === '/contact';

  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
      setMobileMenuOpen(false);
    }
  };

  // ── Bloc login/user (desktop) ─────────────────────────────────────────────
  const AuthDesktop = () =>
    isAuthenticated ? (
      <div className="flex items-center gap-2">
        {user?.picture && (
          <img src={user.picture} alt={user.name}
            className="w-8 h-8 rounded-full object-cover border border-primary/30" />
        )}
        <span className="text-sm text-foreground/80 max-w-[120px] truncate hidden lg:block">
          {user?.name || user?.email}
        </span>
        <button
          onClick={logout}
          title="Se déconnecter"
          className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground
            transition-colors px-2 py-1.5 rounded-lg hover:bg-white/5">
          <LogOut className="w-4 h-4" />
          <span className="hidden lg:inline">Se déconnecter</span>
        </button>
      </div>
    ) : (
      <button
        onClick={() => setShowLogin(true)}
        className="flex items-center gap-1.5 text-sm font-medium text-muted-foreground
          hover:text-primary transition-colors px-3 py-1.5 rounded-lg border border-border/40
          hover:border-primary/40 hover:bg-primary/5">
        <LogIn className="w-4 h-4" />
        Se connecter
      </button>
    );

  // ── Bloc login/user (mobile) ──────────────────────────────────────────────
  const AuthMobile = () =>
    isAuthenticated ? (
      <div className="flex items-center justify-between pt-2 border-t border-border/30">
        <div className="flex items-center gap-2">
          {user?.picture && (
            <img src={user.picture} alt={user.name}
              className="w-7 h-7 rounded-full object-cover border border-primary/30" />
          )}
          <span className="text-sm text-foreground/70 truncate max-w-[160px]">
            {user?.name || user?.email}
          </span>
        </div>
        <button
          onClick={() => { logout(); setMobileMenuOpen(false); }}
          className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors">
          <LogOut className="w-4 h-4" />
          Déconnexion
        </button>
      </div>
    ) : (
      <button
        onClick={() => { setShowLogin(true); setMobileMenuOpen(false); }}
        className="flex items-center gap-2 w-full px-3 py-2.5 rounded-lg border border-primary/30
          text-sm font-medium text-primary hover:bg-primary/10 transition-colors">
        <LogIn className="w-4 h-4" />
        Se connecter
      </button>
    );

  return (
    <>
      <header
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          scrolled ? 'glass shadow-lg' : 'bg-transparent'
        }`}
      >
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            {/* Logo */}
            <Link to="/" className="flex items-center gap-3 hover:opacity-90 transition-opacity">
              <div className="relative">
                <Shield className="w-8 h-8 text-primary" strokeWidth={2} />
                <div className="absolute inset-0 glow-purple opacity-50" />
              </div>
              <span className="text-xl font-bold">
                <span className="text-gradient-purple">Auth0</span>
                <span className="text-foreground">.Expert</span>
              </span>
            </Link>

            {/* Desktop Navigation */}
            <nav className="hidden md:flex items-center gap-6">
              {isOnLanding && (
                <>
                  <button onClick={() => scrollToSection('pillars')}
                    className="text-sm text-muted-foreground hover:text-foreground transition-colors">
                    Piliers
                  </button>
                  <button onClick={() => scrollToSection('protocol')}
                    className="text-sm text-muted-foreground hover:text-foreground transition-colors">
                    Protocole
                  </button>
                  <button onClick={() => scrollToSection('expertise')}
                    className="text-sm text-muted-foreground hover:text-foreground transition-colors">
                    Expertise
                  </button>
                </>
              )}
              <Link to="/blog"
                className={`flex items-center gap-1.5 text-sm font-medium transition-colors
                  ${isOnBlog ? 'text-primary' : 'text-muted-foreground hover:text-foreground'}`}>
                <BookOpen className="w-4 h-4" />
                Blog
              </Link>
              <Link to="/contact"
                className={`flex items-center gap-1.5 text-sm font-medium transition-colors
                  ${isOnContact ? 'text-primary' : 'text-muted-foreground hover:text-foreground'}`}>
                <Mail className="w-4 h-4" />
                Contact
              </Link>

              {/* Séparateur */}
              <div className="w-px h-5 bg-border/50" />

              {/* Login / User */}
              <AuthDesktop />

              <Button
                onClick={onCTAClick}
                className="bg-primary hover:bg-primary/90 text-primary-foreground glow-purple transition-smooth">
                Diagnostic gratuit
              </Button>
            </nav>

            {/* Mobile Menu Button */}
            <button
              className="md:hidden text-foreground"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}>
              {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>

          {/* Mobile Menu */}
          {mobileMenuOpen && (
            <nav className="md:hidden mt-4 pb-4 flex flex-col gap-4 glass rounded-lg p-4">
              {isOnLanding && (
                <>
                  <button onClick={() => scrollToSection('pillars')}
                    className="text-left text-muted-foreground hover:text-foreground transition-colors">
                    Piliers
                  </button>
                  <button onClick={() => scrollToSection('protocol')}
                    className="text-left text-muted-foreground hover:text-foreground transition-colors">
                    Protocole
                  </button>
                  <button onClick={() => scrollToSection('expertise')}
                    className="text-left text-muted-foreground hover:text-foreground transition-colors">
                    Expertise
                  </button>
                </>
              )}
              <Link to="/blog" onClick={() => setMobileMenuOpen(false)}
                className={`flex items-center gap-2 text-sm font-medium transition-colors
                  ${isOnBlog ? 'text-primary' : 'text-muted-foreground hover:text-foreground'}`}>
                <BookOpen className="w-4 h-4" />
                Blog Technique
              </Link>
              <Link to="/contact" onClick={() => setMobileMenuOpen(false)}
                className={`flex items-center gap-2 text-sm font-medium transition-colors
                  ${isOnContact ? 'text-primary' : 'text-muted-foreground hover:text-foreground'}`}>
                <Mail className="w-4 h-4" />
                Nous Contacter
              </Link>

              {/* Login mobile */}
              <AuthMobile />

              <Button
                onClick={onCTAClick}
                className="bg-primary hover:bg-primary/90 text-primary-foreground w-full">
                Diagnostic gratuit
              </Button>
            </nav>
          )}
        </div>
      </header>

      {/* Modal login */}
      {showLogin && <LoginModal onClose={() => setShowLogin(false)} />}
    </>
  );
}
